import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './api/supabase';
import Layout from './components/layout/Layout';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import ClientsPage from './pages/ClientsPage';
import SalesHistoryPage from './pages/SalesHistoryPage';
import ExpensesPage from './pages/ExpensesPage';
import LoginPage from './pages/LoginPage';
import ProductsPage from './pages/ProductsPage';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

// A Identidade Visual Final "Mil Encantos"
const theme = createTheme({
  palette: {
    primary: {
      main: '#E91E63', // Rosa mais forte e confiante
      light: '#FCE4EC',
      dark: '#AD1457',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#26A69A', // Verde-água para contraste
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8f9fa', // Fundo quase branco
      paper: '#ffffff',
    },
    text: {
      primary: '#212529', // Preto suave
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: '"Quicksand", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightMedium: 700,
    h4: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, color: '#AD1457' },
    h5: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, color: '#AD1457' },
    h6: { fontWeight: 700 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 50, textTransform: 'none', fontWeight: 700, padding: '10px 24px', boxShadow: 'none' }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: 'white', color: '#212529', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)' }
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: { borderTop: '1px solid #eee' }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: { '&.Mui-selected': { color: '#E91E63' } }
      }
    }
  }
});

// Componente Privado para proteger as rotas
const PrivateRoute = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lógica de verificação de sessão
  useEffect(() => {
    // 1. Verifica a sessão existente ao carregar a página
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // 2. Cria um "ouvinte" para eventos de login/logout em tempo real
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    // Limpa o "ouvinte" quando o componente é desmontado
    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Se houver uma sessão, mostra o conteúdo. Senão, redireciona para o login.
  return session ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={
            <PrivateRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/pos" element={<POSPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/sales" element={<SalesHistoryPage />} />
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  {/* Redireciona qualquer rota não encontrada para a dashboard */}
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Layout>
            </PrivateRoute>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;